import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { X, Mail, Heart } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import axios from "axios";

const NewsletterPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Show popup after 10 seconds, but only once per session
    const hasSeenPopup = sessionStorage.getItem("reigniteNewsletterSeen");
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        sessionStorage.setItem("reigniteNewsletterSeen", "true");
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/newsletter-subscribe`, {
        email,
        name: name || null
      });

      toast({
        title: "Welcome to Reignite Newsletter! 🎉",
        description: "You'll receive inspiring stories and health tips monthly.",
      });

      setIsOpen(false);
      setEmail("");
      setName("");
    } catch (error) {
      toast({
        title: "Subscription Error",
        description: "Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="bg-gray-900 border-orange-500/30 text-white max-w-md mx-auto">
        <DialogHeader className="relative">
          <button
            onClick={handleClose}
            className="absolute right-0 top-0 text-white/60 hover:text-white transition-colors duration-200"
          >
            <X size={24} />
          </button>
          <DialogTitle className="text-2xl font-light text-center mb-4">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 bg-orange-500/20 rounded-full mr-3">
                <Mail className="w-6 h-6 text-orange-500" />
              </div>
              <Heart className="w-5 h-5 text-red-500" />
            </div>
            Join the <span className="text-orange-500">Reignite</span> Newsletter
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="text-center">
            <p className="text-white/80 leading-relaxed font-light mb-4">
              Get monthly inspiration, success stories, and practical tips to help you 
              put more <span className="text-orange-500 font-medium">life in your years</span>!
            </p>
            <div className="text-sm text-white/60 space-y-2">
              <p>✨ Resident success stories</p>
              <p>💪 Exercise tips for independence</p>
              <p>🏡 Living your best retirement village life</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="text"
                placeholder="Your Name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-black/40 border-white/20 text-white placeholder:text-white/50 focus:border-orange-500/50 focus:ring-orange-500/25"
              />
            </div>
            <div>
              <Input
                type="email"
                placeholder="Your Email Address*"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-black/40 border-white/20 text-white placeholder:text-white/50 focus:border-orange-500/50 focus:ring-orange-500/25"
              />
            </div>

            <div className="flex space-x-3">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white font-light transition-all duration-300 hover:scale-105"
              >
                {isSubmitting ? "Subscribing..." : "Subscribe Now"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="border-white/30 text-white hover:bg-white/10"
              >
                Maybe Later
              </Button>
            </div>
          </form>

          <p className="text-xs text-white/40 text-center">
            We respect your privacy. Unsubscribe anytime. No spam, just inspiration!
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewsletterPopup;